import { supabase } from '../lib/supabaseClient';
import type { Database } from '../types/supabase';
import type { Department } from './legacyTypes';

type DepartmentRow = Database['public']['Tables']['departments']['Row'];
type DepartmentInsert = Database['public']['Tables']['departments']['Insert'];
type DepartmentUpdate = Database['public']['Tables']['departments']['Update'];

/**
 * Map Supabase DepartmentRow to Department interface
 */
const mapDepartmentRowToDepartment = (row: DepartmentRow): Department => ({
  id: row.id,
  name: row.name,
  code: row.code,
  description: row.description || '',
  leaderId: row.leader_id || undefined,
  leaderName: row.leader_name || undefined,
  staffCount: row.member_count,
  isActive: row.is_active,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

/**
 * Supabase Organisation Service
 * Handles department management
 */
class SupabaseOrganisationService {
  /**
   * Get all departments with their assigned staff
   */
  async getAllDepartments(): Promise<Department[]> {
    const { data, error } = await supabase
      .from('departments')
      .select('*')
      .eq('is_active', true)
      .order('name');

    if (error) {
      console.error('Error fetching departments:', error);
      return [];
    }

    // Map departments and fetch staff for each
    const departments = (data || []).map(mapDepartmentRowToDepartment);

    // Fetch staff for each department in parallel
    const departmentsWithStaff = await Promise.all(
      departments.map(async (dept) => {
        const staff = await this.getDepartmentStaff(dept.id);
        return {
          ...dept,
          assignedStaff: staff,
          staffCount: staff.length, // Use actual count from fetched staff, not database member_count
        };
      })
    );

    return departmentsWithStaff;
  }

  /**
   * Get department by ID
   */
  async getDepartmentById(departmentId: string): Promise<Department | null> {
    const { data, error } = await supabase
      .from('departments')
      .select('*')
      .eq('id', departmentId)
      .single();

    if (error) {
      console.error('Error fetching department:', error);
      return null;
    }

    return data ? mapDepartmentRowToDepartment(data) : null;
  }

  /**
   * Create department
   */
  async createDepartment(department: any): Promise<DepartmentRow | null> {
    // CACHE-BUST-v4: Force Vercel to rebuild this file
    console.log('✅ [FIXED v6-TIMESTAMP-2025-10-12_23-49-44] createDepartment called with input:', department);

    // Fetch leader name if leaderId is provided
    let leaderName = department.leaderName || null;
    if (department.leaderId && !leaderName) {
      const { data: userData } = await supabase
        .from('users')
        .select('full_name')
        .eq('id', department.leaderId)
        .single();

      if (userData) {
        leaderName = userData.full_name;
      }
    }

    // Map camelCase properties to snake_case for database
    const dbDepartment: DepartmentInsert = {
      name: department.name,
      code: department.code,
      description: department.description,
      leader_id: department.leaderId || null,
      leader_name: leaderName,
      is_active: department.isActive !== undefined ? department.isActive : true,
      member_count: department.staffCount || 0,
    };

    // Debug logging to verify correct payload
    console.log('✅ [FIXED v6-TIMESTAMP-2025-10-12_23-49-44] Sending to Supabase with snake_case:', JSON.stringify(dbDepartment, null, 2));
    console.log('✅ [FIXED v6-TIMESTAMP-2025-10-12_23-49-44] Verify is_active (snake_case):', 'is_active' in dbDepartment);

    const { data, error } = await supabase
      .from('departments')
      .insert(dbDepartment)
      .select()
      .single();

    if (error) {
      console.error('❌ [FIXED v6-TIMESTAMP-2025-10-12_23-49-44] Error creating department:', error);
      return null;
    }

    console.log('✅ [FIXED v6-TIMESTAMP-2025-10-12_23-49-44] Department created successfully:', data);
    return data;
  }

  /**
   * Update department
   */
  async updateDepartment(departmentId: string, updates: any): Promise<boolean> {
    // Map camelCase properties to snake_case for database
    const dbUpdates: DepartmentUpdate = {};

    if (updates.name !== undefined) dbUpdates.name = updates.name;
    if (updates.code !== undefined) dbUpdates.code = updates.code;
    if (updates.description !== undefined) dbUpdates.description = updates.description;
    if (updates.isActive !== undefined) dbUpdates.is_active = updates.isActive;
    if (updates.staffCount !== undefined) dbUpdates.member_count = updates.staffCount;

    // Handle leader updates - fetch leader name if only leaderId is provided
    if (updates.leaderId !== undefined) {
      // First, clear any existing leader for this department
      if (updates.leaderId) {
        await supabase
          .from('users')
          .update({
            is_leader: false,
            leader_of_department: null
          })
          .eq('leader_of_department', departmentId);
      }

      if (updates.leaderId && !updates.leaderName) {
        // First check if user exists in users table
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('full_name, roles')
          .eq('id', updates.leaderId)
          .single();

        if (userError) {
          console.log(`User ${updates.leaderId} not found in users table, checking pending_users...`);
        }

        if (userData) {
          // Active user - set them as leader immediately
          dbUpdates.leader_id = updates.leaderId;
          dbUpdates.leader_name = userData.full_name;

          // Add 'Leader' role if not already present and update user's leader status
          const currentRoles = userData.roles || [];
          const updatedRoles = currentRoles.includes('Leader')
            ? currentRoles
            : [...currentRoles, 'Leader'];

          await supabase
            .from('users')
            .update({
              is_leader: true,
              leader_of_department: departmentId,
              roles: updatedRoles
            })
            .eq('id', updates.leaderId);
        } else {
          // Check if this is a pending user
          const { data: pendingUser, error: pendingError } = await supabase
            .from('pending_users')
            .select('full_name')
            .eq('id', updates.leaderId)
            .single();

          if (pendingUser) {
            // Pending user - just set the leader_id and name, they'll be activated when they log in
            console.log(`Setting pending user ${updates.leaderId} as department leader`);
            dbUpdates.leader_id = updates.leaderId;
            dbUpdates.leader_name = pendingUser.full_name;
          } else {
            console.error(`Cannot set leader_id: User ${updates.leaderId} does not exist in users or pending_users`);
            return false;
          }
        }
      } else if (updates.leaderName !== undefined) {
        dbUpdates.leader_name = updates.leaderName;

        // Still need to update the user's leader status
        if (updates.leaderId) {
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('roles')
            .eq('id', updates.leaderId)
            .single();

          if (userError) {
            console.error(`Error fetching user ${updates.leaderId}:`, userError);
          }

          if (userData) {
            // Only set leader_id if user exists
            dbUpdates.leader_id = updates.leaderId;

            const currentRoles = userData.roles || [];
            const updatedRoles = currentRoles.includes('Leader')
              ? currentRoles
              : [...currentRoles, 'Leader'];

            await supabase
              .from('users')
              .update({
                is_leader: true,
                leader_of_department: departmentId,
                roles: updatedRoles
              })
              .eq('id', updates.leaderId);
          } else {
            console.error(`Cannot set leader_id: User ${updates.leaderId} does not exist`);
            return false;
          }
        }
      } else if (updates.leaderId === null) {
        // Allow explicitly setting leader to null
        dbUpdates.leader_id = null;
      }
    }

    const { error } = await supabase
      .from('departments')
      .update(dbUpdates)
      .eq('id', departmentId);

    if (error) {
      console.error('Error updating department:', error);
      return false;
    }

    return true;
  }

  /**
   * Delete department (soft delete - set is_active to false)
   */
  async deleteDepartment(departmentId: string): Promise<boolean> {
    try {
      // First, unassign all staff from this department
      const { error: unassignError } = await supabase
        .from('users')
        .update({
          department_id: null,
          is_leader: false,
          leader_of_department: null
        })
        .eq('department_id', departmentId);

      if (unassignError) {
        console.error('Error unassigning staff:', unassignError);
        return false;
      }

      // Then mark the department as inactive
      const { error: deleteError } = await supabase
        .from('departments')
        .update({ is_active: false })
        .eq('id', departmentId);

      if (deleteError) {
        console.error('Error deleting department:', deleteError);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in deleteDepartment:', error);
      return false;
    }
  }

  /**
   * Unassign user from department (handles both active and pending users)
   */
  async unassignUserFromDepartment(userId: string): Promise<boolean> {
    try {
      let oldDepartmentId: string | null = null;

      // Try to get user from users table
      const { data: userData, error: fetchError } = await supabase
        .from('users')
        .select('is_leader, leader_of_department, department_id')
        .eq('id', userId)
        .single();

      // If not in users table, check pending_users table
      if (fetchError || !userData) {
        const { data: pendingData, error: pendingError } = await supabase
          .from('pending_users')
          .select('department_id, roles')
          .eq('id', userId)
          .single();

        if (!pendingError && pendingData) {
          oldDepartmentId = pendingData.department_id;

          // Unassign pending user from department
          const { error: unassignError } = await supabase
            .from('pending_users')
            .update({ department_id: null })
            .eq('id', userId);

          if (unassignError) {
            console.error('Error unassigning pending user:', unassignError);
            return false;
          }

          // Update old department member count
          if (oldDepartmentId) {
            await this.updateDepartmentMemberCount(oldDepartmentId);
          }

          return true;
        }

        console.error('Error fetching user:', fetchError);
        return false;
      }

      oldDepartmentId = userData.department_id;

      // Unassign active user from department
      const { error: unassignError } = await supabase
        .from('users')
        .update({
          department_id: null,
          is_leader: false,
          leader_of_department: null
        })
        .eq('id', userId);

      if (unassignError) {
        console.error('Error unassigning user:', unassignError);
        return false;
      }

      // If user was a leader, clear the department's leader_id
      if (userData?.is_leader && userData?.leader_of_department) {
        const { error: deptError } = await supabase
          .from('departments')
          .update({ leader_id: null })
          .eq('id', userData.leader_of_department);

        if (deptError) {
          console.error('Error updating department:', deptError);
          // Don't return false here, as the user was successfully unassigned
        }
      }

      // Update old department member count
      if (oldDepartmentId) {
        await this.updateDepartmentMemberCount(oldDepartmentId);
      }

      return true;
    } catch (error) {
      console.error('Error in unassignUserFromDepartment:', error);
      return false;
    }
  }

  /**
   * Update department member count (includes both active and pending users)
   */
  async updateDepartmentMemberCount(departmentId: string): Promise<void> {
    try {
      // Count active users
      const { count: activeCount, error: activeError } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('department_id', departmentId);

      if (activeError) {
        console.error('Error counting active users:', activeError);
      }

      // Count pending users
      const { count: pendingCount, error: pendingError } = await supabase
        .from('pending_users')
        .select('*', { count: 'exact', head: true })
        .eq('department_id', departmentId);

      if (pendingError) {
        console.error('Error counting pending users:', pendingError);
      }

      // Total count
      const totalCount = (activeCount || 0) + (pendingCount || 0);

      // Update department member_count
      const { error: updateError } = await supabase
        .from('departments')
        .update({ member_count: totalCount })
        .eq('id', departmentId);

      if (updateError) {
        console.error('Error updating department member count:', updateError);
      }
    } catch (error) {
      console.error('Error in updateDepartmentMemberCount:', error);
    }
  }

  /**
   * Assign user to department (handles both active and pending users)
   */
  async assignUserToDepartment(
    userId: string,
    departmentId: string,
    isLeader: boolean = false
  ): Promise<boolean> {
    try {
      // First, unassign user from current department (this also updates old dept count)
      await this.unassignUserFromDepartment(userId);

      // Check if user is in users table or pending_users table
      const { data: userCheck } = await supabase
        .from('users')
        .select('id')
        .eq('id', userId)
        .single();

      if (!userCheck) {
        // User is pending, update pending_users table
        const { error: assignError } = await supabase
          .from('pending_users')
          .update({ department_id: departmentId })
          .eq('id', userId);

        if (assignError) {
          console.error('Error assigning pending user:', assignError);
          return false;
        }

        // Update new department member count
        await this.updateDepartmentMemberCount(departmentId);

        return true;
      }

      // User is active, assign to new department
      const { error: assignError } = await supabase
        .from('users')
        .update({ department_id: departmentId })
        .eq('id', userId);

      if (assignError) {
        console.error('Error assigning user:', assignError);
        return false;
      }

      // If user should be leader, update their leader status
      if (isLeader) {
        // Clear any existing leader for this department
        await supabase
          .from('users')
          .update({
            is_leader: false,
            leader_of_department: null
          })
          .eq('leader_of_department', departmentId);

        // Get user's current roles
        const { data: userData } = await supabase
          .from('users')
          .select('roles')
          .eq('id', userId)
          .single();

        // Add 'Leader' role if not already present
        const currentRoles = userData?.roles || [];
        const updatedRoles = currentRoles.includes('Leader')
          ? currentRoles
          : [...currentRoles, 'Leader'];

        // Set new leader with Leader role
        const { error: leaderError } = await supabase
          .from('users')
          .update({
            is_leader: true,
            leader_of_department: departmentId,
            roles: updatedRoles
          })
          .eq('id', userId);

        if (leaderError) {
          console.error('Error setting leader:', leaderError);
          return false;
        }

        // Update department's leader_id
        const { error: deptLeaderError } = await supabase
          .from('departments')
          .update({ leader_id: userId })
          .eq('id', departmentId);

        if (deptLeaderError) {
          console.error('Error updating department leader:', deptLeaderError);
          return false;
        }
      }

      // Update new department member count
      await this.updateDepartmentMemberCount(departmentId);

      return true;
    } catch (error) {
      console.error('Error in assignUserToDepartment:', error);
      return false;
    }
  }

  /**
   * Get all staff members in a department (including pending users)
   */
  async getDepartmentStaff(departmentId: string): Promise<any[]> {
    try {
      // Fetch active users in this department
      const { data: activeUsers, error: usersError } = await supabase
        .from('users')
        .select('*')
        .eq('department_id', departmentId)
        .order('full_name');

      if (usersError) {
        console.error('Error fetching department staff:', usersError);
      }

      // Fetch pending users assigned to this department
      const { data: pendingUsers, error: pendingError } = await supabase
        .from('pending_users')
        .select('*')
        .eq('department_id', departmentId)
        .order('full_name');

      if (pendingError) {
        console.error('Error fetching pending department staff:', pendingError);
      }

      // Transform active users to include isPending flag
      const transformedActiveUsers = (activeUsers || []).map((user: any) => ({
        ...user,
        isPending: false,
        uid: user.id,
      }));

      // Transform pending users to match user structure
      const transformedPendingUsers = (pendingUsers || []).map((pending: any) => ({
        id: pending.id,
        uid: pending.id,
        full_name: pending.full_name,
        email: pending.email,
        employee_id: pending.employee_id,
        department: null,
        department_id: pending.department_id,
        departmentId: pending.department_id,
        roles: pending.roles,
        status: pending.status,
        is_leader: pending.roles.includes('Leader'),
        isLeader: pending.roles.includes('Leader'),
        leader_of_department: pending.roles.includes('Leader') ? pending.department_id : null,
        phone_number: pending.phone_number,
        start_date: null,
        til_balance: pending.til_balance,
        tilBalance: pending.til_balance,
        created_at: pending.created_at,
        updated_at: pending.updated_at,
        last_login_at: null,
        isPending: true,
        username: '',
        password: '',
      }));

      // Combine both lists
      const allStaff = [...transformedActiveUsers, ...transformedPendingUsers];

      // Sort by full_name
      allStaff.sort((a, b) => a.full_name.localeCompare(b.full_name));

      return allStaff;
    } catch (error) {
      console.error('Error in getDepartmentStaff:', error);
      return [];
    }
  }

  /**
   * Assign multiple users to a department
   */
  async assignMultipleUsersToDepartment(
    userIds: string[],
    departmentId: string,
    leaderId?: string
  ): Promise<boolean> {
    try {
      // Get the department details to get the name
      const { data: dept, error: deptError } = await supabase
        .from('departments')
        .select('name, id')
        .eq('id', departmentId)
        .single();

      if (deptError || !dept) {
        console.error('Error fetching department:', deptError);
        return false;
      }

      console.log('Assigning users to department:', dept.name, 'ID:', dept.id);
      console.log('User IDs:', userIds);

      // First, unassign all users from their current departments
      const { error: unassignError } = await supabase
        .from('users')
        .update({
          department_id: null,
          department: null,
          is_leader: false,
          leader_of_department: null
        })
        .in('id', userIds);

      if (unassignError) {
        console.error('Error unassigning users:', unassignError);
        return false;
      }

      // Then assign all users to the new department (with denormalized name)
      const { error: assignError } = await supabase
        .from('users')
        .update({
          department_id: departmentId,
          department: dept.name  // Also update the denormalized department name
        })
        .in('id', userIds);

      if (assignError) {
        console.error('Error assigning users:', assignError);
        return false;
      }

      console.log('Users assigned successfully');

      // If a leader is specified, update their leader status
      if (leaderId && userIds.includes(leaderId)) {
        // First, get the user's current roles
        const { data: leaderData, error: leaderFetchError } = await supabase
          .from('users')
          .select('roles')
          .eq('id', leaderId)
          .single();

        if (leaderFetchError) {
          console.error('Error fetching leader data:', leaderFetchError);
          return false;
        }

        // Add 'Leader' role if not already present
        const currentRoles = leaderData?.roles || [];
        const updatedRoles = currentRoles.includes('Leader')
          ? currentRoles
          : [...currentRoles, 'Leader'];

        // Update leader status and add Leader role
        const { error: leaderError } = await supabase
          .from('users')
          .update({
            is_leader: true,
            leader_of_department: departmentId,
            roles: updatedRoles
          })
          .eq('id', leaderId);

        if (leaderError) {
          console.error('Error setting leader:', leaderError);
          return false;
        }

        // Update the department's leader_id and leader_name
        const leaderName = leaderData ?
          (await supabase.from('users').select('full_name').eq('id', leaderId).single()).data?.full_name
          : null;

        const { error: deptLeaderError } = await supabase
          .from('departments')
          .update({
            leader_id: leaderId,
            leader_name: leaderName
          })
          .eq('id', departmentId);

        if (deptLeaderError) {
          console.error('Error updating department leader:', deptLeaderError);
          return false;
        }
      }

      // Recalculate staff count for the department
      const { count, error: countError } = await supabase
        .from('users')
        .select('id', { count: 'exact', head: true })
        .eq('department_id', departmentId);

      if (!countError && count !== null) {
        await supabase
          .from('departments')
          .update({ member_count: count })
          .eq('id', departmentId);

        console.log('Updated member count:', count);
      }

      return true;
    } catch (error) {
      console.error('Error in assignMultipleUsersToDepartment:', error);
      return false;
    }
  }
}

export const supabaseOrganisationService = new SupabaseOrganisationService();
export default supabaseOrganisationService;
